import { Component, Input, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, Subject, of, takeUntil } from 'rxjs';
import { PromotionModel } from 'src/app/models/promotion-model';
import { UploadPhotoModel } from 'src/app/models/upload-photo-model';
import { PromotionLogoComponentStore } from 'src/app/stores/components/promotion-logo/promotion-logo.component-store';
import * as _ from 'lodash';

@Component({
    selector: 'app-promotion-logo',
    templateUrl: './promotion-logo.component.html',
    styleUrls: ['./promotion-logo.component.scss'],
    standalone: false
})
export class PromotionLogoComponent implements OnInit {
  readonly max_size = 538 * 1024;
  readonly allowed_types = ['image/png', 'image/jpeg'];
  readonly min_height = 512;
  readonly min_width = 512;
  readonly max_height = 512;
  readonly max_width = 512;
  readonly placeholderUrl = '/assets/img/placeholder.png';

  @Input()
  promotion: PromotionModel;

  ngUnsubscribeState = new Subject<void>();

  isImageSaved: boolean;

  promotionLogoUrl$: Observable<string>;

  constructor(private promotionLogoComponentStore: PromotionLogoComponentStore,
    private snackBar: MatSnackBar
  ) {
    // Do nothing
  }

  ngOnInit(): void {
    this.promotionLogoComponentStore.logo$.pipe(takeUntil(this.ngUnsubscribeState)).subscribe(logo => {
      this.snackBar.dismiss();
      if (logo !== null && logo !== undefined) {
        this.isImageSaved = true;
        this.promotionLogoUrl$ = of(logo.url);
      }
      else {
        this.isImageSaved = false;
        this.promotionLogoUrl$ = of(this.placeholderUrl);
        if (this.promotion
          && (this.promotion.isActive || this.promotion.isPendingActivation)) {
          this.snackBar.open('Please upload a promotion logo.', 'Dismiss');
        }
      }
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribeState.next();
    this.ngUnsubscribeState.complete();
  }

  ngOnChanges(changes) {
    if (!changes.promotion.firstChange) {
      if (this.promotion) {
        this.promotionLogoComponentStore.getPromotionLogo({ companyId: this.promotion.company.id, promotionId: this.promotion.id });
      }
    }
  }

  fileChangeEvent(fileInput: any) {
    if (fileInput.target.files && fileInput.target.files[0]) {
      if (fileInput.target.files[0].size > this.max_size) {
        const errorMessage =
          'Maximum size allowed is ' + Math.floor(this.max_size / 1000) + 'Kb';
        this.snackBar.open(errorMessage, "Dismiss");
        return false;
      }

      if (!_.includes(this.allowed_types, fileInput.target.files[0].type)) {
        const errorMessage = 'Only images are allowed ( JPG | PNG )';
        this.snackBar.open(errorMessage, "Dismiss");
        return false;
      }

      const uploadPhoto = new UploadPhotoModel();

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const image = new Image();
        image.src = e.target.result;
        image.onload = rs => {
          const img_height = rs.currentTarget['height'];
          const img_width = rs.currentTarget['width'];
          if (img_height > this.max_height || img_width > this.max_width) {
            const errorMessage =
              'Dimensions required ' +
              this.max_height +
              '*' +
              this.max_width +
              'px';
            this.snackBar.open(errorMessage, "Dismiss");
            return false;
          } 
          else if (img_height < this.min_height || img_width < this.min_width) {
            const errorMessage =
              'Dimensions required ' +
              this.min_height +
              '*' +
              this.min_width +
              'px';
            this.snackBar.open(errorMessage, "Dismiss");
            return false;
          } else {
            const imgBase64Path = e.target.result;
            uploadPhoto.fileContentBase64 = imgBase64Path.split(',')[1];
            this.promotionLogoComponentStore.uploadPromotionLogo({ companyId: this.promotion.company.id, promotionId: this.promotion.id, uploadPhoto: uploadPhoto });

            return true;
          }
        };
      };

      const logo = fileInput.target.files[0];

      uploadPhoto.title = 'Promotion Logo';
      uploadPhoto.fileName = 'logo' + this._determineFileExtension(logo.type);

      reader.readAsDataURL(logo);
      return true;
    }
    return true;
  }

  removeImage() {
    this.promotionLogoComponentStore.deletePromotionLogo({ companyId: this.promotion.company.id, promotionId: this.promotion.id });
  }

  _determineFileExtension(fileType: string): string {
    switch (fileType) {
      case 'image/jpg':
      case 'image/jpeg':
        return '.jpg';
      case 'image/png':
        return '.png';
    }
    throw new Error('Unsupported file type:' + fileType);
  }
}
